/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
@file:Suppress("MatchingDeclarationName")

package studio.oddpods.cauce.feature.sessions

import androidx.navigation.NavController
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavOptions
import androidx.navigation.toRoute
import kotlinx.serialization.Serializable
import studio.oddpods.cauce.core.base.ui.composableWithPushTransitions

@Serializable
data class LogSessionRoute(val patientId: String? = null)

fun NavController.navigateToLogSession(patientId: String? = null, navOptions: NavOptions? = null) {
    navigate(LogSessionRoute(patientId), navOptions)
}

fun NavGraphBuilder.logSessionDestination(
    onBackClick: () -> Unit,
) {
    composableWithPushTransitions<LogSessionRoute> {
        val route = it.toRoute<LogSessionRoute>()
        LogSessionScreen(
            onBackClick = onBackClick,
            preselectedPatientId = route.patientId,
        )
    }
}

@Serializable
data class CreateRecurringScheduleRoute(val patientId: String? = null)

fun NavController.navigateToCreateRecurringSchedule(patientId: String? = null, navOptions: NavOptions? = null) {
    navigate(CreateRecurringScheduleRoute(patientId), navOptions)
}

fun NavGraphBuilder.createRecurringScheduleDestination(
    onBackClick: () -> Unit,
) {
    composableWithPushTransitions<CreateRecurringScheduleRoute> {
        val route = it.toRoute<CreateRecurringScheduleRoute>()
        CreateRecurringScheduleScreen(
            onBackClick = onBackClick,
            preselectedPatientId = route.patientId,
        )
    }
}

@Serializable
data class SessionDetailRoute(val sessionId: String)

fun NavController.navigateToSessionDetail(sessionId: String, navOptions: NavOptions? = null) {
    navigate(SessionDetailRoute(sessionId), navOptions)
}

fun NavGraphBuilder.sessionDetailDestination(
    onBackClick: () -> Unit,
    onEditClick: (sessionId: String) -> Unit,
) {
    composableWithPushTransitions<SessionDetailRoute> {
        val route = it.toRoute<SessionDetailRoute>()
        SessionDetailScreen(
            sessionId = route.sessionId,
            onBackClick = onBackClick,
            onEditClick = { onEditClick(route.sessionId) },
        )
    }
}

@Serializable
data class EditSessionRoute(val sessionId: String)

fun NavController.navigateToEditSession(sessionId: String, navOptions: NavOptions? = null) {
    navigate(EditSessionRoute(sessionId), navOptions)
}

fun NavGraphBuilder.editSessionDestination(
    onBackClick: () -> Unit,
) {
    composableWithPushTransitions<EditSessionRoute> {
        val route = it.toRoute<EditSessionRoute>()
        EditSessionScreen(
            sessionId = route.sessionId,
            onBackClick = onBackClick,
        )
    }
}

@Serializable
data class EditRecurringScheduleRoute(val scheduleId: String, val patientName: String)

fun NavController.navigateToEditRecurringSchedule(
    scheduleId: String,
    patientName: String,
    navOptions: NavOptions? = null,
) {
    navigate(EditRecurringScheduleRoute(scheduleId, patientName), navOptions)
}

fun NavGraphBuilder.editRecurringScheduleDestination(
    onBackClick: () -> Unit,
) {
    composableWithPushTransitions<EditRecurringScheduleRoute> {
        val route = it.toRoute<EditRecurringScheduleRoute>()
        EditRecurringScheduleScreen(
            scheduleId = route.scheduleId,
            patientName = route.patientName,
            onBackClick = onBackClick,
        )
    }
}
